import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LandingPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Auto-redirect returning users to their dashboard
  useEffect(() => {
    const companyId = localStorage.getItem("olana_company_id");
    if (companyId) navigate(`/dashboard/${companyId}`, { replace: true });
  }, [navigate]);

  const handleStart = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/users`, { email });
      localStorage.setItem("olana_user_id", data.id);
      localStorage.setItem("olana_email", data.email);
      navigate("/onboarding");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('/bg-nature.jpg')`,
        }}
      />
      <div className="absolute inset-0 bg-black/75" />

      {/* Aurora glow */}
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-accent/5 blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-2xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="text-center"
        >
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Leaf className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Olana
            </h1>
          </div>

          {/* Tagline */}
          <p className="text-lg md:text-xl text-white/60 leading-relaxed mb-3 tracking-wide uppercase text-sm" data-testid="landing-caption">
            Progressive Sustainability Intelligence
          </p>
          <p className="text-base md:text-lg text-white/40 mb-12 max-w-lg mx-auto leading-relaxed">
            Understand your company's impact without heavy reporting.
            Get personalized insights in minutes.
          </p>

          {/* Email input */}
          <div className="space-y-4 max-w-md mx-auto">
            <input
              data-testid="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleStart()}
              placeholder="Enter your work email"
              className="hero-input"
              autoFocus
            />

            <Button
              data-testid="get-started-btn"
              onClick={handleStart}
              disabled={loading}
              className="w-full h-14 rounded-full bg-primary text-primary-foreground text-lg font-medium btn-glow disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  Starting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Begin your journey
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </div>

          <p className="text-xs text-white/20 mt-8">
            No credit card required. Takes less than 5 minutes.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
